import React, { useState, useRef, useCallback } from 'react';
import { useLocation } from '../contexts/LocationContext';
import { usePermissions } from '../contexts/PermissionsContext';
import useAccountingData from '../components/accounting/useAccountingData';
import api from '../services/api';
import toast from 'react-hot-toast';
import { tabsConfig } from '../components/accounting/accountingConstants';
import OverviewTab from '../components/accounting/OverviewTab';
import InvoicesTab from '../components/accounting/InvoicesTab';
import PaymentsTab from '../components/accounting/PaymentsTab';
import CashFlowTab from '../components/accounting/CashFlowTab';
import CostsTab from '../components/accounting/CostsTab';
import ProfitabilityTab from '../components/accounting/ProfitabilityTab';
import WydatkiTab from '../components/accounting/WydatkiTab';
import { KosztyKsefPanel } from '../components/koszty';

function CardValidationTab({ locationId }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, matched, unmatched_csv, unmatched_pos
  const csvRef = useRef(null);

  const handleUpload = useCallback(async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('csv_file', file);
      const res = await api.post('/accounting/card-validation', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000
      });
      if (res.data?.success) {
        setResult(res.data.data);
        toast.success(res.data.message);
      } else {
        toast.error(res.data?.error || 'Blad');
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Blad importu CSV');
    } finally { setLoading(false); }
  }, []);

  const s = result?.summary || {};
  const fmt = (v) => new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(v || 0);

  return (
    <div>
      {/* Upload */}
      <div style={{ backgroundColor: 'var(--card-bg)', padding: '1rem', borderRadius: '6px', marginBottom: '1rem', border: '1px solid var(--border)' }}>
        <h6 style={{ margin: '0 0 0.5rem', fontSize: '13px', fontWeight: '600' }}>Walidacja platnosci kartami</h6>
        <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0 0 0.75rem' }}>
          Wgraj plik CSV od operatora kart. System porowana transakcje kartowe z paragonami w systemie (kwota + data +-10 min).
        </p>
        <input ref={csvRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => { handleUpload(e.target.files[0]); e.target.value = ''; }} />
        <button onClick={() => csvRef.current?.click()} disabled={loading} style={{
          padding: '6px 12px', fontSize: '12px', fontWeight: '600', border: '2px solid var(--info)',
          cursor: 'pointer', color: 'var(--info)', backgroundColor: 'var(--card-bg)'
        }}>
          {loading ? 'Analizowanie...' : 'Wgraj plik CSV'}
        </button>
      </div>

      {/* Wyniki */}
      {result && (
        <>
          {/* Podsumowanie */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem', marginBottom: '1rem' }}>
            {[
              { label: 'Transakcje CSV', value: s.csv_count, amount: fmt(s.csv_total), color: 'var(--info)' },
              { label: 'Paragony system', value: s.pos_count, amount: fmt(s.pos_total), color: 'var(--purple)' },
              { label: 'Dopasowane', value: s.matched_count, amount: fmt(s.matched_total), color: 'var(--success)' },
              { label: 'Brak w systemie', value: s.unmatched_csv_count, amount: '', color: 'var(--danger)' },
              { label: 'Brak w CSV', value: s.unmatched_pos_count, amount: '', color: 'var(--warning)' },
              { label: 'Roznica', value: '', amount: fmt(s.difference), color: Math.abs(s.difference || 0) < 0.01 ? 'var(--success)' : 'var(--danger)' },
            ].map((item, i) => (
              <div key={i} style={{ backgroundColor: 'var(--card-bg)', padding: '0.75rem', borderRadius: '4px', borderLeft: `3px solid ${item.color}`, textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: item.color }}>{item.value !== '' ? item.value : item.amount}</div>
                {item.amount && item.value !== '' && <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{item.amount}</div>}
                <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>{item.label}</div>
              </div>
            ))}
          </div>

          {/* Per lokalizacja */}
          {s.per_location && Object.keys(s.per_location).length > 0 && (
            <div style={{ backgroundColor: 'var(--card-bg)', padding: '0.75rem', borderRadius: '6px', marginBottom: '1rem' }}>
              <h6 style={{ margin: '0 0 0.5rem', fontSize: '12px', fontWeight: '600' }}>Per lokalizacja</h6>
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Lokalizacja', 'CSV (il.)', 'CSV (kwota)', 'System (il.)', 'System (kwota)', 'Dopasowane', 'Roznica'].map(h => (
                      <th key={h} style={{ padding: '0.4rem', borderBottom: '2px solid var(--border)', textAlign: 'left', fontSize: '10px', fontWeight: '600' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(s.per_location).map(([locId, loc]) => (
                    <tr key={locId} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '0.3rem 0.4rem', fontWeight: '500' }}>{loc.nazwa}</td>
                      <td style={{ padding: '0.3rem 0.4rem' }}>{loc.csv_count}</td>
                      <td style={{ padding: '0.3rem 0.4rem' }}>{fmt(loc.csv_total)}</td>
                      <td style={{ padding: '0.3rem 0.4rem' }}>{loc.pos_count}</td>
                      <td style={{ padding: '0.3rem 0.4rem' }}>{fmt(loc.pos_total)}</td>
                      <td style={{ padding: '0.3rem 0.4rem', color: 'var(--success)', fontWeight: '600' }}>{loc.matched}</td>
                      <td style={{ padding: '0.3rem 0.4rem', fontWeight: '600', color: Math.abs(loc.csv_total - loc.pos_total) < 0.01 ? 'var(--success)' : 'var(--danger)' }}>
                        {fmt(loc.csv_total - loc.pos_total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Filtry */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '0.75rem' }}>
            {[
              { key: 'all', label: `Wszystkie (${(result.matched?.length || 0) + (result.unmatched_csv?.length || 0) + (result.unmatched_pos?.length || 0)})` },
              { key: 'matched', label: `Dopasowane (${result.matched?.length || 0})` },
              { key: 'unmatched_csv', label: `Brak w systemie (${result.unmatched_csv?.length || 0})` },
              { key: 'unmatched_pos', label: `Brak w CSV (${result.unmatched_pos?.length || 0})` },
            ].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} style={{
                padding: '4px 10px', fontSize: '11px', cursor: 'pointer',
                border: filter === f.key ? '1px solid var(--primary)' : '1px solid var(--border)',
                backgroundColor: filter === f.key ? 'var(--primary)' : 'var(--card-bg)',
                color: filter === f.key ? '#fff' : 'var(--text-secondary)', fontWeight: filter === f.key ? '700' : '500'
              }}>{f.label}</button>
            ))}
          </div>

          {/* Tabela szczegolowa */}
          <div style={{ maxHeight: '400px', overflowY: 'auto', backgroundColor: 'var(--card-bg)', borderRadius: '6px', border: '1px solid var(--border)' }}>
            <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)' }}>
                <tr>
                  {['LP', 'Status', 'Kwota', 'Data CSV', 'Data System', 'Karta', 'Paragon', 'Kasjer', 'Placowka'].map(h => (
                    <th key={h} style={{ padding: '0.4rem', borderBottom: '2px solid var(--border)', textAlign: 'left', fontSize: '9px', fontWeight: '600' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Dopasowane */}
                {(filter === 'all' || filter === 'matched') && (result.matched || []).map((m, idx) => (
                  <tr key={`m-${idx}`} style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'rgba(40,167,69,0.05)' }}>
                    <td style={{ padding: '0.3rem 0.4rem', color: 'var(--text-muted)' }}>{idx + 1}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}><span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '3px', backgroundColor: 'var(--success)', color: '#fff', fontWeight: '600' }}>OK</span></td>
                    <td style={{ padding: '0.3rem 0.4rem', fontWeight: '600' }}>{fmt(m.csv.kwota)}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>{m.csv.data}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>{m.pos.data}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>{m.csv.karta}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}><code style={{ fontSize: '9px' }}>{m.pos.paragon}</code></td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>{m.pos.kasjer}</td>
                    <td style={{ padding: '0.3rem 0.4rem', fontSize: '9px' }}>{m.csv.placowka?.substring(0, 30)}</td>
                  </tr>
                ))}
                {/* Brak w systemie */}
                {(filter === 'all' || filter === 'unmatched_csv') && (result.unmatched_csv || []).map((u, idx) => (
                  <tr key={`uc-${idx}`} style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'rgba(220,53,69,0.05)' }}>
                    <td style={{ padding: '0.3rem 0.4rem', color: 'var(--text-muted)' }}>{idx + 1}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}><span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '3px', backgroundColor: 'var(--danger)', color: '#fff', fontWeight: '600' }}>BRAK W SYSTEMIE</span></td>
                    <td style={{ padding: '0.3rem 0.4rem', fontWeight: '600' }}>{fmt(u.kwota)}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>{u.data}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>-</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>{u.karta}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>-</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>-</td>
                    <td style={{ padding: '0.3rem 0.4rem', fontSize: '9px' }}>{u.placowka?.substring(0, 30)}</td>
                  </tr>
                ))}
                {/* Brak w CSV */}
                {(filter === 'all' || filter === 'unmatched_pos') && (result.unmatched_pos || []).map((u, idx) => (
                  <tr key={`up-${idx}`} style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'rgba(255,193,7,0.05)' }}>
                    <td style={{ padding: '0.3rem 0.4rem', color: 'var(--text-muted)' }}>{idx + 1}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}><span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '3px', backgroundColor: 'var(--warning)', color: '#fff', fontWeight: '600' }}>BRAK W CSV</span></td>
                    <td style={{ padding: '0.3rem 0.4rem', fontWeight: '600' }}>{fmt(u.kwota)}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>-</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>{u.data}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>-</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}><code style={{ fontSize: '9px' }}>{u.paragon}</code></td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>{u.kasjer}</td>
                    <td style={{ padding: '0.3rem 0.4rem' }}>-</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function AccountingPage() {
  const { locationId } = useLocation() || {};
  const { hasPermission } = usePermissions();
  const canManageSettlements = hasPermission('ksiegowosc', 'rozliczenia', locationId);
  const [activeTab, setActiveTab] = useState('overview');

  const data = useAccountingData({ locationId, activeTab });
  const overviewStats = data.calculateOverviewStats();

  return (
    <div>
      {/* Header */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Moduł Księgowy</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Faktury, płatności i przepływy finansowe</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={data.selectedFilterLocation}
              onChange={(e) => data.setSelectedFilterLocation(e.target.value)}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid var(--border-color, var(--border))',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                fontSize: '12px',
                cursor: 'pointer',
                minWidth: '150px'
              }}
            >
              <option value="">Wszystkie lokalizacje</option>
              {data.locations.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.nazwa}
                </option>
              ))}
            </select>

            <button
              className="btn btn-primary"
              onClick={data.refreshAll}
            >
              Odśwież
            </button>
          </div>
        </div>
      </div>

      <div>
        {/* Zakladki */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{
            display: 'flex',
            gap: '4px',
            backgroundColor: 'var(--border)',
            padding: '4px',
            borderRadius: '6px',
            width: 'fit-content'
          }}>
            {tabsConfig.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: activeTab === tab.id ? 'var(--success)' : 'transparent',
                  color: activeTab === tab.id ? '#fff' : 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Zawartosc zakladek */}
        {data.loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '32px', marginBottom: '1rem' }}>{'⏳'}</div>
            <div>Ładowanie danych...</div>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <OverviewTab
                overviewStats={overviewStats}
                kasaSaldo={data.kasaSaldo}
                allInvoices={data.allInvoices}
                kasaOperations={data.kasaOperations}
                markingPaid={data.markingPaid}
                markAsPaid={data.markAsPaid}
                setInvoiceFilters={data.setInvoiceFilters}
                setActiveTab={setActiveTab}
              />
            )}
            {activeTab === 'invoices' && (
              <InvoicesTab
                invoices={data.invoices}
                invoiceFilters={data.invoiceFilters}
                setInvoiceFilters={data.setInvoiceFilters}
                pagination={data.pagination}
                locations={data.locations}
                markingPaid={data.markingPaid}
                markAsPaid={data.markAsPaid}
              />
            )}
            {activeTab === 'payments' && (
              <PaymentsTab
                allInvoices={data.allInvoices}
                markingPaid={data.markingPaid}
                markAsPaid={data.markAsPaid}
              />
            )}
            {activeTab === 'cash-flow' && (
              <CashFlowTab
                kasaOperations={data.kasaOperations}
                kasaFilters={data.kasaFilters}
                setKasaFilters={data.setKasaFilters}
              />
            )}
            {activeTab === 'costs' && (
              <CostsTab
                costs={data.costs}
                costCategories={data.costCategories}
                costsLoading={data.costsLoading}
                costsFilters={data.costsFilters}
                setCostsFilters={data.setCostsFilters}
                showCostModal={data.showCostModal}
                editingCost={data.editingCost}
                costForm={data.costForm}
                setCostForm={data.setCostForm}
                locations={data.locations}
                canManageSettlements={canManageSettlements}
                handleSaveCost={data.handleSaveCost}
                handlePayCost={data.handlePayCost}
                handleDeleteCost={data.handleDeleteCost}
                openEditCost={data.openEditCost}
                openNewCost={data.openNewCost}
                closeCostModal={data.closeCostModal}
                fetchCosts={data.fetchCosts}
              />
            )}
            {activeTab === 'koszty-ksef' && (
              <KosztyKsefPanel sklepy={data.locations} locationId={data.selectedFilterLocation || ''} onZaksiegowano={data.fetchCosts} />
            )}

            {activeTab === 'card-validation' && (
              <CardValidationTab locationId={locationId} />
            )}
            {activeTab === 'wydatki' && (
              <WydatkiTab locations={data.locations} initialLocationId={data.selectedFilterLocation} />
            )}
            {activeTab === 'profitability' && (
              <ProfitabilityTab
                profitabilityData={data.profitabilityData}
                profitabilityLoading={data.profitabilityLoading}
                profDateFrom={data.profDateFrom}
                setProfDateFrom={data.setProfDateFrom}
                profDateTo={data.profDateTo}
                setProfDateTo={data.setProfDateTo}
                fetchProfitability={data.fetchProfitability}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
